import React from 'react';
import {
  PHASE_LABELS, GENERIC_JOB_ERROR, transitionErrorText, isSupersedable, routeName, needsVerification, canCancel, stoppedSlotsText,
} from '../lib/phoneDriverTransition';

/*
 * The phone-wide #128 route job on the slot settings page (lib/phoneDriverTransition.js has the contract).
 *
 * Actions, each shown only where the server allows it (job view fields), all disabled while any request is pending so
 * nothing is sent twice:
 *   Retry safely       retryAllowed                 the same route again under a new operation
 *   Cancel switch      cancelAllowed and NOT        unclaimed request, or FAILED / EXPIRED before any phone change -
 *                      verificationRequired         never a job that replaced an operation which changed the phone (MF-1)
 *   Re-check / Switch  FAILED / EXPIRED (supersede) saves #128 again with that value: a new verified job
 */
export default function PhoneDriverTransitionNotice({
  transition, onRetry, onCancel, onSaveRoute, canSaveRoute = true, retrying = false, cancelling = false, savingRoute = false, busy = false,
}) {
  if (!transition) return null;
  const pending = busy || retrying || cancelling || savingRoute;
  const supersede = isSupersedable(transition);
  const current = routeName(transition.effectiveShared);
  const other = routeName(!transition.effectiveShared);
  const code = transition.errorCode;
  const showRouteButtons = supersede && !!onSaveRoute && canSaveRoute;
  // MF-1: the cancel wording and the button come from the server's verificationRequired flag; "nothing was changed" is
  // said only when the server explicitly reports verificationRequired=false.
  const verify = needsVerification(transition);
  const cancellable = canCancel(transition);
  const untouched = cancellable && transition.verificationRequired === false;
  const stoppedSlots = stoppedSlotsText(transition);
  return <div role="status" data-testid="phone-driver-transition" className="rounded-xl border border-border p-4 space-y-2">
    <p className="font-medium">{PHASE_LABELS[transition.phase] || 'Phone driver switch pending'}</p>
    <p className="text-sm">Requested: {routeName(transition.requestedShared)}. Current: {current}.</p>
    <p className="text-sm text-muted-foreground">This change applies to every slot on this phone. Your pause settings stay as they are.</p>
    {code && <p className="text-sm">{transitionErrorText(code) || GENERIC_JOB_ERROR} <span className="text-muted-foreground">({code})</span></p>}
    {supersede && <p className="text-sm" data-testid="phone-driver-transition-recovery">
      To recover, save setting #128 again with the route you want, ON or OFF. Either value starts a new switch that the
      desktop verifies before this phone&rsquo;s slots restart; saving the current route ({current}) re-checks its connection.
      {untouched ? ' Nothing on the phone was changed yet, so you can also cancel and keep ' + current + ' as it is.' : ''}
      {verify ? ' The phone may already have been changed by this or an earlier switch, so it cannot be cancelled: only a verified switch releases it.' : ''}
    </p>}
    {verify && !supersede && transition.held && <p className="text-sm" data-testid="phone-driver-transition-verification">
      The phone may already have been changed by an earlier switch, so this one re-checks it before any slot on it runs
      again and cannot be cancelled. Keep the desktop client open so it can finish.
    </p>}
    {stoppedSlots && <p className="text-sm" data-testid="phone-driver-transition-stopped-slots">{stoppedSlots}</p>}
    {(transition.retryAllowed || cancellable || showRouteButtons) && <div className="flex flex-wrap gap-2">
      {transition.retryAllowed && <button type="button" disabled={pending} onClick={onRetry} className="rounded border px-3 py-2" data-testid="phone-driver-retry">
        {retrying ? 'Requesting…' : 'Retry safely'}</button>}
      {showRouteButtons && <button type="button" disabled={pending} onClick={() => onSaveRoute(!!transition.effectiveShared)} className="rounded border px-3 py-2" data-testid="phone-driver-recheck">
        {savingRoute ? 'Saving…' : `Re-check ${current} (save #128 again)`}</button>}
      {showRouteButtons && <button type="button" disabled={pending} onClick={() => onSaveRoute(!transition.effectiveShared)} className="rounded border px-3 py-2" data-testid="phone-driver-switch-other">
        {savingRoute ? 'Saving…' : `Switch to ${other}`}</button>}
      {cancellable && onCancel && <button type="button" disabled={pending} onClick={onCancel} className="rounded border px-3 py-2" data-testid="phone-driver-cancel">
        {cancelling ? 'Cancelling…' : 'Cancel switch'}</button>}
    </div>}
  </div>;
}
